import { useId, type ButtonHTMLAttributes, type ReactNode, type Ref } from "react";
import { Icon, type IconName } from "./Icon";
import "./menu-item.css";

type Props = Omit<ButtonHTMLAttributes<HTMLButtonElement>, "children" | "role" | "aria-labelledby" | "aria-describedby"> & {
  /** Il nome della voce. */
  label: ReactNode;
  /** Una riga piccola sotto il nome, che spiega che cosa fa: arriva come descrizione, non come nome. */
  hint?: ReactNode;
  /** Un'icona prima del nome (le voci di «Altro» della scheda). */
  icon?: IconName;
  /** Arriva al `<button>` (in React 19 `ref` è una prop). */
  ref?: Ref<HTMLButtonElement>;
};

// Una voce di un menu aperto da `useMenuButton` (`menu-button.ts`): un `button` con `role="menuitem"`,
// che il menu trova da sé per le frecce. Con `hint` il nome è la sola prima riga e la seconda è la descrizione.
export function MenuItem({ label, hint, icon, type = "button", className, ...rest }: Props) {
  const id = useId();
  const labelId = `${id}-label`;
  const hintId = `${id}-hint`;
  const withHint = hint !== undefined && hint !== null;
  return (
    <button
      type={type}
      role="menuitem"
      className={className ? `g-menu-item ${className}` : "g-menu-item"}
      data-hint={withHint ? "true" : undefined}
      aria-labelledby={withHint ? labelId : undefined}
      aria-describedby={withHint ? hintId : undefined}
      {...rest}
    >
      {icon ? <Icon name={icon} /> : null}
      <span id={labelId} className="g-menu-item__label">{label}</span>
      {withHint ? <span id={hintId} className="g-menu-item__hint">{hint}</span> : null}
    </button>
  );
}
